import React, { useEffect, useState } from 'react';
import {
  PenTool, Layout, Palette, Image as ImageIcon, Sparkles, 
  Layers, ArrowRight, CheckCircle2, MonitorPlay
} from 'lucide-react';
import SEO from './SEO';

const Graphicdesign = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsVisible(true);
  }, []);

  return (
    <div className="bg-[#050810] min-h-screen text-white overflow-hidden selection:bg-[#0EA5E9]/30" style={{ fontFamily: 'emoji' }}>
      <SEO
        title="Graphic Design Services in India – Vishwa DigiTech"
        description="Creative graphic design services by Vishwa DigiTech. We design logos, brochures, social media creatives, banners & brand identity. Professional designers delivering premium visual content."
        keywords="graphic design sangli, logo design india, brochure design sangli, social media graphics, brand identity design maharashtra, creative design agency india"
        canonicalPath="/services/graphics-designing"
      />
      
      {/* Custom Animations & Styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(2deg); }
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-delayed { animation: float 6s ease-in-out 3s infinite; }
        
        .glass-card {
          background: rgba(255, 255, 255, 0.02);
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.05);
          transition: all 0.4s ease;
        }
        .glass-card:hover {
          background: rgba(255, 255, 255, 0.04);
          border-color: rgba(14, 165, 233, 0.3);
          transform: translateY(-5px);
          box-shadow: 0 10px 40px -10px rgba(14, 165, 233, 0.2);
        }
        
        .text-gradient {
          background: linear-gradient(135deg, #0EA5E9 0%, #7DD3FC 50%, #0EA5E9 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      {/* Decorative Background Orbs for Design Vibe (Purple & Pink) */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden flex justify-center items-center">
        <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#0EA5E9]/10 rounded-full blur-[150px] animate-float" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-[#1A4A8B]/20 rounded-full blur-[150px] animate-float-delayed" />
        <div className="absolute top-[40%] left-[40%] w-[400px] h-[400px] bg-[#0EA5E9]/10 rounded-full blur-[120px] animate-pulse" />
      </div>

      <div className="relative z-10">
        
        {/* HERO SECTION */}
        <section className="pt-32 pb-24 px-4 max-w-7xl mx-auto flex flex-col items-center text-center">
          <div className={`transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0EA5E9]/10 border border-[#0EA5E9]/30 shadow-sm backdrop-blur-md mb-8">
              <Sparkles className="w-4 h-4 text-[#0EA5E9]" />
              <span className="text-sm font-bold tracking-widest text-[#0EA5E9] uppercase">Creative Studio</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-8 leading-[1.1] text-white">
              Design That <br className="hidden md:block"/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Speaks & Converts.</span>
            </h1>
            
            <p className="text-lg md:text-2xl text-gray-400 max-w-3xl mx-auto font-light mb-12 leading-relaxed">
              We craft stunning visual identities and high-converting creative assets that make your brand impossible to ignore.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-5 justify-center items-center w-full sm:w-auto">
              <button className="px-10 py-4 bg-gradient-to-r from-[#0EA5E9] to-[#0284c7] text-white rounded-xl font-medium text-lg hover:from-[#0284c7] hover:to-[#0369a1] transition-all shadow-[0_0_30px_rgba(14,165,233,0.4)] flex items-center justify-center gap-3 w-full sm:w-auto group hover:scale-105">
                Explore Portfolio <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button className="px-10 py-4 bg-white/5 border border-white/10 text-white rounded-xl font-medium text-lg hover:bg-white/10 transition-colors flex items-center justify-center gap-3 w-full sm:w-auto">
                <PenTool className="w-5 h-5 text-[#0EA5E9]" /> Start a Project
              </button>
            </div>
          </div>
        </section>

        {/* FLOATING TOOLS SHOWCASE */}
        <section className="py-10 overflow-hidden relative">
           <div className="absolute inset-0 bg-gradient-to-r from-[#050810] via-transparent to-[#050810] z-10 pointer-events-none"></div>
           <div className="flex gap-12 justify-center opacity-40 items-center h-20">
             <img src="https://upload.wikimedia.org/wikipedia/commons/a/af/Adobe_Photoshop_CC_icon.svg" alt="Photoshop" className="w-12 h-12 grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300 cursor-pointer" />
             <img src="https://upload.wikimedia.org/wikipedia/commons/f/fb/Adobe_Illustrator_CC_icon.svg" alt="Illustrator" className="w-12 h-12 grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300 cursor-pointer" />
             <img src="https://upload.wikimedia.org/wikipedia/commons/3/33/Figma-logo.svg" alt="Figma" className="w-10 h-14 grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300 cursor-pointer" />
             <img src="https://upload.wikimedia.org/wikipedia/commons/4/40/Adobe_Premiere_Pro_CC_icon.svg" alt="Premiere" className="w-12 h-12 grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300 cursor-pointer" />
             <img src="https://upload.wikimedia.org/wikipedia/commons/c/cb/Adobe_After_Effects_CC_icon.svg" alt="After Effects" className="w-12 h-12 grayscale hover:grayscale-0 hover:scale-110 transition-all duration-300 cursor-pointer" />
           </div>
        </section>

        {/* SERVICES GRID */}
        <section className="py-24 px-4 max-w-7xl mx-auto">
          <div className="text-center mb-20 flex flex-col items-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-8 backdrop-blur-md animate-text-reveal">
              <Sparkles className="w-4 h-4" />
              Our Design Expertise
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Our Design <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Expertise</span></h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#0EA5E9] to-transparent mx-auto mb-6"></div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">From blank canvas to brand dominance. We deliver premium assets across every digital and physical touchpoint.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Service 1 */}
            <div className="glass-card p-8 rounded-3xl relative overflow-hidden group">
              <div className="w-14 h-14 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-2xl flex items-center justify-center mb-6 border border-[#0EA5E9]/20 group-hover:bg-[#0EA5E9] group-hover:text-white transition-all duration-500 shadow-lg">
                <PenTool className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Brand Identity & Logo</h3>
              <p className="text-gray-400 leading-relaxed">Logos, typography, color palettes, and complete brand guidelines that give your business a unique, unforgettable personality.</p>
            </div>

            {/* Service 2 */}
            <div className="glass-card p-8 rounded-3xl relative overflow-hidden group">
              <div className="w-14 h-14 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-2xl flex items-center justify-center mb-6 border border-[#0EA5E9]/20 group-hover:bg-[#0EA5E9] group-hover:text-white transition-all duration-500 shadow-lg">
                <Layout className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">UI/UX & Web Design</h3>
              <p className="text-gray-400 leading-relaxed">Pixel-perfect website mockups and app interfaces designed in Figma to maximize user retention and conversion rates.</p>
            </div>

            {/* Service 3 */}
            <div className="glass-card p-8 rounded-3xl relative overflow-hidden group">
              <div className="w-14 h-14 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-2xl flex items-center justify-center mb-6 border border-[#0EA5E9]/20 group-hover:bg-[#0EA5E9] group-hover:text-white transition-all duration-500 shadow-lg">
                <ImageIcon className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Social Media Creatives</h3>
              <p className="text-gray-400 leading-relaxed">Thumb-stopping Instagram carousels, Facebook ad banners, and LinkedIn graphics tailored for high engagement.</p>
            </div>

            {/* Service 4 */}
            <div className="glass-card p-8 rounded-3xl relative overflow-hidden group">
              <div className="w-14 h-14 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-2xl flex items-center justify-center mb-6 border border-[#0EA5E9]/20 group-hover:bg-[#0EA5E9] group-hover:text-white transition-all duration-500 shadow-lg">
                <Layers className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Print & Packaging</h3>
              <p className="text-gray-400 leading-relaxed">Business cards, brochures, and product packaging designs that feel premium in the hands of your customers.</p>
            </div>

            {/* Service 5 */}
            <div className="glass-card p-8 rounded-3xl relative overflow-hidden group">
              <div className="w-14 h-14 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-2xl flex items-center justify-center mb-6 border border-[#0EA5E9]/20 group-hover:bg-[#0EA5E9] group-hover:text-white transition-all duration-500 shadow-lg">
                <MonitorPlay className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Motion Graphics</h3>
              <p className="text-gray-400 leading-relaxed">Engaging logo reveals, explainer videos, and subtle UI animations that bring your static designs to life.</p>
            </div>

            {/* Service 6 */}
            <div className="glass-card p-8 rounded-3xl relative overflow-hidden group">
              <div className="w-14 h-14 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-2xl flex items-center justify-center mb-6 border border-[#0EA5E9]/20 group-hover:bg-[#0EA5E9] group-hover:text-white transition-all duration-500 shadow-lg">
                <Sparkles className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Ad Campaign Creatives</h3>
              <p className="text-gray-400 leading-relaxed">High-converting visuals engineered specifically for performance marketing to reduce CPC and boost click-through rates.</p>
            </div>

          </div>
        </section>

        {/* VISUAL IMPACT SPLIT SECTION */}
        <section className="py-24 px-4 max-w-7xl mx-auto">
          <div className="glass-card rounded-[3rem] p-8 md:p-16 flex flex-col lg:flex-row items-center gap-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#0EA5E9]/10 to-transparent pointer-events-none"></div>
            
            <div className="lg:w-1/2 relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">Good design is <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">good business.</span></h2>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">Users form an opinion about your brand in 0.05 seconds. We ensure that first impression communicates trust, authority, and premium quality.</p>
              
              <div className="space-y-6">
                {[
                  "Visual storytelling that aligns with your brand voice.",
                  "Consistent aesthetics across all platforms.",
                  "Designs backed by consumer psychology."
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-8 h-8 rounded-full bg-[#0EA5E9]/10 flex items-center justify-center border border-[#0EA5E9]/20 shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-[#0EA5E9]" />
                    </div>
                    <span className="text-gray-300 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:w-1/2 relative z-10 w-full">
              <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl group">
                <img src="https://images.unsplash.com/photo-1626785774573-4b799315345d?q=80&w=2071&auto=format&fit=crop" alt="Graphic Design Process" className="w-full h-[400px] object-cover transform group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050810] via-transparent to-transparent opacity-80 pointer-events-none"></div>
                
                {/* Floating UI Elements */}
                <div className="absolute bottom-6 left-6 right-6 bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0EA5E9] rounded-lg flex items-center justify-center">
                      <Palette className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white">Brand Kit .AI</p>
                      <p className="text-xs text-gray-300">Ready for export</p>
                    </div>
                  </div>
                  <div className="text-[#0EA5E9] font-bold">100%</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CREATIVE PROCESS */}
        <section className="py-24 px-4 max-w-7xl mx-auto border-t border-white/5 mt-10">
          <div className="text-center mb-16 flex flex-col items-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-sm font-bold tracking-wider uppercase mb-8 backdrop-blur-md animate-text-reveal">
              <Sparkles className="w-4 h-4" />
              Creative Process
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] via-[#7DD3FC] to-[#0EA5E9]">Creative Process</span></h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#0EA5E9] to-transparent mx-auto mb-6"></div>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light">A structured approach to ensure flawless execution and pure creativity.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-[#0EA5E9]/10 via-[#0EA5E9]/30 to-[#0EA5E9]/10 -translate-y-1/2 z-0"></div>
            
            {[
              { step: "01", title: "Discovery", desc: "Understanding your vision, target audience, and market positioning." },
              { step: "02", title: "Ideation", desc: "Brainstorming concepts, mood boards, and initial wireframes." },
              { step: "03", title: "Creation", desc: "Designing high-fidelity drafts with meticulous attention to detail." },
              { step: "04", title: "Delivery", desc: "Final revisions and handing over all source files and assets." }
            ].map((item, idx) => (
              <div key={idx} className="relative z-10 bg-[#0A0F1F] border border-white/5 p-8 rounded-3xl hover:border-[#0EA5E9]/30 transition-colors shadow-[0_0_20px_rgba(14,165,233,0.05)] hover:shadow-[0_0_20px_rgba(14,165,233,0.15)] group">
                <span className="text-5xl font-black text-white/5 absolute top-4 right-4 group-hover:text-[#0EA5E9]/10 transition-colors">{item.step}</span>
                <div className="w-4 h-4 rounded-full bg-[#0EA5E9] shadow-[0_0_15px_rgba(14,165,233,0.8)] mb-6 hidden md:block absolute -top-10 left-1/2 -translate-x-1/2"></div>
                <h3 className="text-xl font-bold text-white mb-3 mt-4 relative z-10 group-hover:text-[#0EA5E9] transition-colors">{item.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed relative z-10">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>


      </div>
    </div>
  );
};

export default Graphicdesign;
